import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { ActiveCartFacade, Cart } from '@spartacus/cart/base/root';
import { AuthService, AuthStorageService, BaseSiteService, UserIdService } from '@spartacus/core';
import { BehaviorSubject, Observable, Subject, throwError } from 'rxjs';
import { catchError, map, pluck, publishReplay, refCount, retry, take, tap } from 'rxjs/operators';
import { PostBillingAddressPayload, PostPlacedOrderPayload } from 'src/app/model/checkout.model';
import { ASM_ENABLED_LOCAL_STORAGE_KEY } from 'src/app/spartacus/features/asm/asm-constant';
import { environment } from 'src/environments/environment';
import { ComponentService } from '../component.service';
import { StorageService } from '../storage/storage.service';

@Injectable({
  providedIn: 'root',
})
export class CheckoutServiceExtends {
  // Observables
  baseSite$: Observable<string> = this.baseService.getActive();
  activeCart$: Observable<string> = this.cartService.getActiveCartId();
  userCurrent$: Observable<string> = this.userIdService.getUserId();

  // Other Properties
  occUrl = this.meta.getTag('name=occ-backend-base-url');
  url = `${this.occUrl.content}${environment.prefix}${this.baseSiteId}`;
  headers = new HttpHeaders().set('Content-Type', 'application/json');
  asmEnabled = this.storageService.getLocalStorageItem(ASM_ENABLED_LOCAL_STORAGE_KEY);
  cacheWindowTime = 3600000;
  private cachedCheckoutFeaturesConfig$: any = {};
  CHECKOUT_FEATURES_CONFIG = '../../../public/features/CHECKOUT_FEATURES.json';

  // Subjects
  private placerOrders = new Subject<any>();
  private orderTypeData = new BehaviorSubject<any>(null);
  private totalSummaryData = new BehaviorSubject<any>(null);
  isQueryOrderTypeStable = new BehaviorSubject<boolean>(false);
  placeOrder$ = this.placerOrders.asObservable();
  orderType$ = this.orderTypeData.asObservable();
  totalSummary$ = this.totalSummaryData.asObservable();

  constructor(
    protected cartService: ActiveCartFacade,
    protected http: HttpClient,
    protected auth: AuthService,
    protected userIdService: UserIdService,
    protected authStorageService: AuthStorageService,
    protected baseService: BaseSiteService,
    protected componentService: ComponentService,
    private storageService: StorageService,
    private meta: Meta
  ) {}

  updateOrderType(passedOrderTypeData: any) {
    this.orderTypeData.next(passedOrderTypeData);
  }

  updateTotalSummary(passedTotalSummaryData: any) {
    this.totalSummaryData.next(passedTotalSummaryData);
  }

  setOrders(status: boolean) {
    this.placerOrders.next(status);
  }

  get baseSiteId(): string {
    let baseSiteId: string;
    this.baseSite$.pipe(take(1)).subscribe((baseSiteData) => (baseSiteId = baseSiteData));
    return baseSiteId;
  }
  get userCurrent(): string {
    let userCurrent: string;
    this.userCurrent$.pipe(take(1)).subscribe((userId) => (userCurrent = userId));
    return userCurrent;
  }
  get tokenId(): string {
    let tokenId: string;
    this.authStorageService
      .getToken()
      .pipe(take(1))
      .subscribe((tokenIdData) => (tokenId = tokenIdData.access_token));
    return tokenId;
  }
  get activeCartId() {
    let activeCartId: string;
    this.activeCart$.pipe(take(1)).subscribe((activeCart) => (activeCartId = activeCart));
    return activeCartId;
  }

  queryOrderType(orderTypeId: any): Observable<Cart> {
    this.isQueryOrderTypeStable.next(false);
    return this.http
      .post(`${this.url}/users/${this.userCurrent}/carts/${this.activeCartId}/orderType?orderType=${orderTypeId}`, {
        headers: this.headers,
      })
      .pipe(
        retry(1),
        tap(() => this.isQueryOrderTypeStable.next(true)),
        catchError(this.errorHandl)
      );
  }

  postSetBillingAddressToPayment(payload: PostBillingAddressPayload): Observable<any> {
    return this.http
      .put(
        // tslint:disable-next-line: max-line-length
        `${this.url}/users/${this.userCurrent}/carts/${payload.cartId}/addresses/payment?addressId=${payload.addressId}`,
        {
          headers: this.headers,
        }
      )
      .pipe(retry(1), catchError(this.errorHandl)); //tslint:disable-line
  }

  postCheckoutPlaceOrder(payload: PostPlacedOrderPayload): Observable<any> {
    // need to make it dynamic
    const body = this.buildPlaceOrderBody(payload);
    const params = new HttpParams()
      .set('fields', payload.fields)
      .set('cartId', payload.cartId)
      .set('securityCode', payload.securityCode);

    return this.http
      .post(`${this.url}/users/${this.userCurrent}/orders`, JSON.stringify(body), {
        params,
        headers: this.headers,
      })
      .pipe(map((data) => data));
  }

  queryDatesMinMax(): Observable<any> {
    return this.http
      .get(`${this.url}/users/${this.userCurrent}/carts/checkoutConfig`)
      .pipe(retry(1), catchError(this.errorHandl));
  }

  getCheckoutFeaturesConfig$(activeCountry: string): Observable<any> {
    if (!this.cachedCheckoutFeaturesConfig$[activeCountry]) {
      this.cachedCheckoutFeaturesConfig$[activeCountry] = this.http
        .get(this.CHECKOUT_FEATURES_CONFIG)
        .pipe(publishReplay(1, this.cacheWindowTime), refCount(), take(1), catchError(this.errorHandl));
    }
    return this.cachedCheckoutFeaturesConfig$[activeCountry];
  }

  isCheckoutFeatureEnabled$(activeCountry: string, feature: string): Observable<boolean> {
    const idString = [activeCountry, feature].join();
    if (!this.cachedCheckoutFeaturesConfig$[idString]) {
      this.cachedCheckoutFeaturesConfig$[idString] = this.getCheckoutFeaturesConfig$(activeCountry).pipe(
        publishReplay(1, this.cacheWindowTime),
        refCount(),
        take(1),
        pluck('country'),
        map((data: any) => (data[activeCountry] !== undefined ? data[activeCountry] : data['default'])),
        pluck('feature'),
        map((data) => data[feature]['enabled']),
        catchError(this.errorHandl)
      );
    }
    return this.cachedCheckoutFeaturesConfig$[idString];
  }

  errorHandl(error: any) {
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      // Get client-side error
      errorMessage = error.error.message;
    } else {
      // Get server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    return throwError(errorMessage);
  }

  setCheckoutFormsError(formId: string, error: boolean): void {
    this.componentService.checkoutFormsError$.next({
      ...this.componentService.checkoutFormsError$.value,
      [formId]: error,
    });
  }

  buildPlaceOrderBody(payload: PostPlacedOrderPayload): any {
    const body = {
      orderType: payload.orderType,
      additionalEmail: payload.additionalEmail,
      requestDeliveryDate: payload.requestDeliveryDate,
      requestCancelOrderDate: payload.requestCancelOrderDate,
      deliverFullOrder: payload.deliverFullOrder,
      termsChecked: payload.termsChecked,
      purchaseNumber: payload.purchaseNumber,
      deliveryMode: payload.deliveryMode,
      paymentMode: payload.paymentMode,
    };

    if (!body.deliveryMode.code) delete body.deliveryMode; // discard deliveryMode if undefined
    if (!body.paymentMode.code) delete body.paymentMode; // discard paymentMode if undefined

    return body;
  }
}
